import { supabase } from "./supabase";

// ─────────────────────────────────────────────────────────────────────────────
// Auth bridge (Phase 1).
//
// Identity is now Supabase Auth (hashed, managed) — replacing the prototype's
// plaintext-password localStorage login (spec §4/§5). The per-user game state
// helper lives in ./gameState (pure, no Supabase import) and is re-exported here.
// ─────────────────────────────────────────────────────────────────────────────

export { defaultGameState, CURRENT_SEASON } from "./gameState.js";

/** Read the app-facing profile (display name, avatar, admin role) from Postgres. */
export async function fetchProfile(userId) {
  const { data, error } = await supabase
    .from("profiles")
    .select("display_name, avatar, is_admin, supported_team_id, season_year, team_confirmed_season")
    .eq("id", userId)
    .single();
  if (error) {
    console.warn("[auth] could not load profile:", error.message);
    return null;
  }
  return data;
}

/**
 * Auth emails must land back on the origin where the action was taken —
 * local dev, a preview deploy, or production alike. The project's redirect
 * allow-list covers these origins; without an explicit emailRedirectTo the
 * links fall back to the project's Site URL, which bit staging when it was
 * still the localhost default (decisions.md 2026-08-24). Reuse this for any
 * future redirect-carrying auth call (password reset, email change, OTP).
 */
export function authRedirectOrigin() {
  return typeof window === "undefined" ? undefined : window.location.origin;
}

/** Sign in with email + password. Returns { error } on failure. */
export async function signIn(email, password) {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error };
}

/**
 * Register a new account. Display name is stored in user metadata; the DB
 * trigger creates the matching profiles row (and flags the first admin).
 */
export async function signUp(email, password, name) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: { data: { name }, emailRedirectTo: authRedirectOrigin() },
  });
  return { data, error, needsConfirmation: !error && !data.session };
}

export async function signOut() {
  await supabase.auth.signOut();
}

/** Send a password reset email back to the acting origin. Returns { error }. */
export async function requestPasswordReset(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: authRedirectOrigin() });
  return { error };
}

/** Update the signed-in user's password through Supabase Auth. Returns { error }. */
export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error };
}

/** Translate common Auth failures into actionable copy; preserve unknown messages. */
export function friendlyAuthError(error) {
  if (!error) return "";
  const message = error.message || "";
  if (error.status === 429 || /rate limit|security purposes/i.test(message)) return "Too many emails sent just now — try again in an hour.";
  if (/Invalid login credentials/i.test(message)) return "That email or password isn't right. Check both, or use Forgot password.";
  if (/Email not confirmed/i.test(message)) return "Confirm your email first — check your inbox for the confirmation link.";
  if (/Password should be at least/i.test(message)) return "Use at least 6 characters.";
  if (/registration_closed/i.test(message)) return "New sign-ups are closed for now.";
  return message;
}
